// File: frontend/src/components/BusinessList.js
import React, { useState } from 'react';
import {
  Building2, Search, Plus, Edit2, Trash2, Grid3x3, Table,
  Globe, Phone, Mail, Users, MapPin, Save, X, Download, UserPlus, Link as LinkIcon, Briefcase
} from 'lucide-react';
import { businessAPI } from '../utils/api';
import { useData } from '../contexts/DataContext';
import { useUI } from '../contexts/UIContext';
import BulkBusinessRelationshipTool from './BulkBusinessRelationshipTool';

const STATUS_OPTIONS = ['active', 'inactive', 'dissolved'];
const TYPE_OPTIONS = ['Corporation', 'LLC', 'Partnership', 'Sole Proprietorship', 'Non-Profit', 'Government', 'Other'];
const INDUSTRY_OPTIONS = [
  'Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail',
  'Real Estate', 'Construction', 'Transportation', 'Education',
  'Entertainment', 'Hospitality', 'Other'
];

const statusColor = (status) => {
  switch (status) {
    case 'active':   return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400';
    case 'inactive': return 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200';
    case 'dissolved': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400';
    default:         return 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200';
  }
};

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

// ── Table View ────────────────────────────────────────────────────────────────
const BusinessTableView = ({ businesses, people, onEdit, onDelete }) => {
  const { refreshCoreData } = useData();
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAdd, setQuickAdd] = useState({ name: '', type: '', industry: '', status: 'active' });
  const [showBulkRelationships, setShowBulkRelationships] = useState(false);

  const startEdit = (b) => {
    setEditingRow(b.id);
    setEditData({ name: b.name, type: b.type || '', industry: b.industry || '', status: b.status || 'active' });
  };

  const saveEdit = async (id) => {
    try {
      const original = businesses.find(b => b.id === id);
      await businessAPI.update(id, { ...original, ...editData });
      setEditingRow(null);
      refreshCoreData();
    } catch (e) {
      alert('Failed to update: ' + e.message);
    }
  };

  const handleQuickAdd = async () => {
    if (!quickAdd.name.trim()) { alert('Business name is required'); return; }
    try {
      await businessAPI.create({ ...quickAdd, employees: [] });
      setQuickAdd({ name: '', type: '', industry: '', status: 'active' });
      setShowQuickAdd(false);
      refreshCoreData();
    } catch (e) {
      alert('Failed to create: ' + e.message);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Type', 'Industry', 'Status', 'Owner', 'City', 'Country', 'Phone', 'Email', 'Linked People'];
    const rows = businesses.map(b => [
      b.name || '',
      b.type || '',
      b.industry || '',
      b.status || '',
      b.owner_name?.trim() || '',
      b.city || '',
      b.country || '',
      b.phone || '',
      b.email || '',
      (b.linked_people || []).length
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `businesses-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const inp = 'px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm dark:bg-slate-700 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 backdrop-blur-xl border border-gray-300 dark:border-gray-600 shadow-glass-lg rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowQuickAdd(!showQuickAdd)}
            className="px-4 py-2 bg-blue-600 text-white dark:bg-blue-500 rounded-lg hover:shadow-glow-md transition-all flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" /><span>Quick Add</span>
          </button>
          <button
            onClick={() => setShowBulkRelationships(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center space-x-2"
          >
            <LinkIcon className="w-4 h-4" /><span>Bulk Relationships</span>
          </button>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-slate-300 rounded-lg hover:shadow-md transition-all flex items-center space-x-2"
          >
            <Download className="w-4 h-4" /><span>Export CSV</span>
          </button>
        </div>
      </div>

      {showBulkRelationships && (
        <BulkBusinessRelationshipTool
          onClose={() => setShowBulkRelationships(false)}
          people={people}
          businesses={businesses}
          onComplete={refreshCoreData}
        />
      )}

      {/* Quick Add Row */}
      {showQuickAdd && (
        <div className="bg-blue-50/50 dark:bg-slate-800 border border-blue-300 rounded-lg p-4">
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <input type="text" placeholder="Business Name *" value={quickAdd.name}
              onChange={e => setQuickAdd({ ...quickAdd, name: e.target.value })}
              className="flex-1 min-w-[160px] px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500" />
            <select value={quickAdd.type} onChange={e => setQuickAdd({ ...quickAdd, type: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100">
              <option value="">Type</option>
              {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={quickAdd.industry} onChange={e => setQuickAdd({ ...quickAdd, industry: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100">
              <option value="">Industry</option>
              {INDUSTRY_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            <select value={quickAdd.status} onChange={e => setQuickAdd({ ...quickAdd, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
            </select>
            <button onClick={handleQuickAdd}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2">
              <UserPlus className="w-4 h-4" /><span>Add</span>
            </button>
            <button onClick={() => setShowQuickAdd(false)} className="p-2 text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 border-b border-gray-200 dark:border-slate-700">
              <tr>
                {['Name', 'Type', 'Industry', 'Status', 'Owner', 'Location', 'Cases', 'Linked People', 'Employed At', 'Actions'].map(h => (
                  <th key={h} className={`px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-slate-300 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {businesses.map((b, idx) => (
                <tr key={b.id} className={`hover:bg-blue-50/30 dark:hover:bg-slate-700/30 transition-colors ${idx % 2 === 0 ? 'bg-white/50 dark:bg-slate-800/50' : 'bg-gray-50/30 dark:bg-slate-900/30'}`}>
                  {/* Name */}
                  <td className="px-4 py-3">
                    {editingRow === b.id
                      ? <input className={inp} value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} />
                      : <div className="flex items-center space-x-2">
                          <div className="w-7 h-7 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                            <Building2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </div>
                          <span className="font-medium text-gray-900 dark:text-slate-100">{b.name}</span>
                        </div>
                    }
                  </td>

                  {/* Type */}
                  <td className="px-4 py-3">
                    {editingRow === b.id
                      ? <select className={inp} value={editData.type} onChange={e => setEditData({ ...editData, type: e.target.value })}>
                          <option value="">—</option>
                          {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      : <span className="text-sm text-gray-600 dark:text-gray-400">{b.type || '—'}</span>
                    }
                  </td>

                  {/* Industry */}
                  <td className="px-4 py-3">
                    {editingRow === b.id
                      ? <select className={inp} value={editData.industry} onChange={e => setEditData({ ...editData, industry: e.target.value })}>
                          <option value="">—</option>
                          {INDUSTRY_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
                        </select>
                      : <span className="text-sm text-gray-600 dark:text-gray-400">{b.industry || '—'}</span>
                    }
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    {editingRow === b.id
                      ? <select className={inp} value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })}>
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
                        </select>
                      : b.status
                        ? <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColor(b.status)}`}>{capitalize(b.status)}</span>
                        : <span className="text-gray-400">—</span>
                    }
                  </td>

                  {/* Owner */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{b.owner_name?.trim() || '—'}</span>
                  </td>

                  {/* Location */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {[b.city, b.country].filter(Boolean).join(', ') || '—'}
                    </span>
                  </td>

                  {/* Cases */}
                  <td className="px-4 py-3">
                    {b.linked_cases?.length > 0
                      ? <div className="flex flex-wrap gap-1">
                          {b.linked_cases.map(c => (
                            <span key={c} className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 font-medium">
                              <Briefcase className="w-3 h-3 mr-1 shrink-0" />{c}
                            </span>
                          ))}
                        </div>
                      : <span className="text-gray-400 text-sm">—</span>
                    }
                  </td>

                  {/* Linked People */}
                  <td className="px-4 py-3">
                    {b.linked_people?.length > 0
                      ? <span className="inline-flex items-center space-x-1 text-sm text-blue-600 dark:text-blue-400 font-medium">
                          <Users className="w-3.5 h-3.5" />
                          <span>{b.linked_people.length}</span>
                        </span>
                      : <span className="text-gray-400 text-sm">—</span>
                    }
                  </td>

                  {/* Employed At */}
                  <td className="px-4 py-3">
                    {b.employed_people?.length > 0
                      ? <span className="inline-flex items-center space-x-1 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                          <Briefcase className="w-3.5 h-3.5" />
                          <span>{b.employed_people.length}</span>
                        </span>
                      : <span className="text-gray-400 text-sm">—</span>
                    }
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end space-x-1">
                      {editingRow === b.id ? (
                        <>
                          <button onClick={() => saveEdit(b.id)} className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded" title="Save"><Save className="w-4 h-4" /></button>
                          <button onClick={() => setEditingRow(null)} className="p-1.5 text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-700 rounded" title="Cancel"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(b)} className="p-1.5 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 rounded" title="Quick Edit"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => onEdit(b)} className="p-1.5 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded" title="Full Edit"><Edit2 className="w-4 h-4" fill="currentColor" /></button>
                          <button onClick={() => onDelete(b.id)} className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {businesses.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-slate-500" />
              <p>No businesses found. Click "Quick Add" to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Card View ─────────────────────────────────────────────────────────────────
const BusinessCardView = ({ businesses, onEdit, onDelete }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {businesses.map(b => (
      <div key={b.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm rounded-lg p-5 hover:shadow-md transition-shadow group">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
              <Building2 className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{b.name}</h3>
              {b.type && <p className="text-sm text-slate-500 dark:text-slate-400">{b.type}</p>}
            </div>
          </div>
          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onEdit(b)} className="p-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-600 hover:text-white transition-colors" title="Edit">
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete(b.id)} className="p-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-red-600 dark:text-red-400 hover:bg-red-600 hover:text-white transition-colors" title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {b.industry && (
            <div className="flex items-center text-sm">
              <Building2 className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
              <span className="text-gray-600 dark:text-gray-400">{b.industry}</span>
            </div>
          )}
          {b.owner_name?.trim() && (
            <div className="flex items-center text-sm">
              <Users className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
              <span className="text-gray-600 dark:text-gray-400">Owner: {b.owner_name.trim()}</span>
            </div>
          )}
          {b.city && (
            <div className="flex items-center text-sm">
              <MapPin className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
              <span className="text-gray-600 dark:text-gray-400">{[b.city, b.state, b.country].filter(Boolean).join(', ')}</span>
            </div>
          )}
          {b.linked_cases?.length > 0 && (
            <div className="flex items-start text-sm">
              <Briefcase className="w-4 h-4 mr-2 text-purple-500 mt-0.5 shrink-0" />
              <div className="flex flex-wrap gap-1">
                {b.linked_cases.map(c => (
                  <span key={c} className="px-2 py-0.5 text-xs rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 font-medium">{c}</span>
                ))}
              </div>
            </div>
          )}
          {b.linked_people?.length > 0 && (
            <div className="flex items-center text-sm">
              <Users className="w-4 h-4 mr-2 text-blue-500" />
              <span className="text-blue-600 dark:text-blue-400 font-medium">{b.linked_people.length} linked {b.linked_people.length === 1 ? 'person' : 'people'}</span>
            </div>
          )}
          {b.employed_people?.length > 0 && (
            <div className="flex items-center text-sm">
              <Briefcase className="w-4 h-4 mr-2 text-emerald-500" />
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">{b.employed_people.length} employed</span>
            </div>
          )}
          <div className="flex items-center space-x-3 text-sm">
            {b.phone && <a href={`tel:${b.phone}`} className="text-blue-600 hover:text-blue-700"><Phone className="w-4 h-4" /></a>}
            {b.email && <a href={`mailto:${b.email}`} className="text-blue-600 hover:text-blue-700"><Mail className="w-4 h-4" /></a>}
            {b.website && <a href={b.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700"><Globe className="w-4 h-4" /></a>}
          </div>
        </div>

        {b.status && (
          <div className="mt-4">
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-lg ${statusColor(b.status)}`}>{capitalize(b.status)}</span>
          </div>
        )}
      </div>
    ))}

    {businesses.length === 0 && (
      <div className="col-span-full text-center py-12">
        <div className="bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg p-8 max-w-md mx-auto">
          <Building2 className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-slate-400 font-medium">No businesses found matching your search criteria.</p>
        </div>
      </div>
    )}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const BusinessList = () => {
  const { businesses, fetchBusinesses, people, refreshCoreData } = useData();
  const { setShowAddBusinessForm, setEditingBusiness } = useUI();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState('table');

  const filtered = businesses.filter(b => {
    const matchSearch = !searchTerm ||
      b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.owner_name && b.owner_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (b.city && b.city.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchIndustry = !filterIndustry || b.industry === filterIndustry;
    const matchStatus = !filterStatus || b.status === filterStatus;
    return matchSearch && matchIndustry && matchStatus;
  });

  const uniqueIndustries = [...new Set(businesses.map(b => b.industry).filter(Boolean))];

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;
    try {
      await businessAPI.delete(id);
      refreshCoreData();
    } catch (e) {
      alert('Failed to delete business');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Business Management</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
            <span className="font-medium">
              {filtered.length === businesses.length ? `${businesses.length} businesses` : `${filtered.length} of ${businesses.length} businesses`}
            </span>
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View toggle */}
          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded flex items-center space-x-2 transition ${viewMode === 'table' ? 'bg-white dark:bg-blue-600 shadow-sm text-blue-600 dark:text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'}`}
              title="Table View"
            >
              <Table className="w-4 h-4" />
              <span className="text-sm font-medium hidden md:inline">Table</span>
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 rounded flex items-center space-x-2 transition ${viewMode === 'cards' ? 'bg-white dark:bg-blue-600 shadow-sm text-blue-600 dark:text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'}`}
              title="Card View"
            >
              <Grid3x3 className="w-4 h-4" />
              <span className="text-sm font-medium hidden md:inline">Cards</span>
            </button>
          </div>

          <button
            onClick={() => setShowAddBusinessForm(true)}
            className="px-6 py-3 bg-blue-600 text-white dark:bg-blue-500 rounded-lg hover:shadow-glow-md transition-[box-shadow] duration-150 flex items-center group active:scale-[0.97]"
          >
            <Plus className="w-5 h-5 mr-2 group-hover:animate-pulse" />
            Add Business
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex space-x-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-green-600 dark:text-green-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, owner, or city..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 glass border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-accent-primary focus:shadow-md dark:bg-slate-800 dark:text-white dark:placeholder-gray-500"
              />
            </div>
          </div>
          <select
            value={filterIndustry}
            onChange={e => setFilterIndustry(e.target.value)}
            className="px-4 py-3 glass border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-accent-primary dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Industries</option>
            {uniqueIndustries.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-4 py-3 glass border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-accent-primary dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
          </select>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        <BusinessTableView
          businesses={filtered}
          people={people}
          onEdit={setEditingBusiness}
          onDelete={handleDelete}
        />
      ) : (
        <BusinessCardView
          businesses={filtered}
          onEdit={setEditingBusiness}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default BusinessList;
